/**
 * Cliente HTTP.
 *
 * Cada llamada adjunta el token de sesión de Clerk. El backend lo verifica de
 * todos modos —la seguridad no depende de este archivo—, pero mandarlo siempre
 * evita 401 espurios y deja una sola forma de hablar con la API.
 */

export type TokenGetter = () => Promise<string | null>

let getToken: TokenGetter = async () => null

export function setTokenGetter(fn: TokenGetter) {
  getToken = fn
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(path, { ...init, headers: { ...headers, ...init?.headers } })
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`
    try {
      detail = (await resp.json()).detail ?? detail
    } catch {
      /* respuesta sin cuerpo JSON */
    }
    throw new ApiError(detail, resp.status)
  }
  return resp.json() as Promise<T>
}

/** Petición sin JSON de por medio: para subir el HTML del reporte y para abrirlo. */
async function requestRaw(path: string, init?: RequestInit): Promise<Response> {
  const token = await getToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const resp = await fetch(path, { ...init, headers: { ...headers, ...init?.headers } })
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`
    try {
      detail = (await resp.json()).detail ?? detail
    } catch {
      /* respuesta sin cuerpo JSON */
    }
    throw new ApiError(detail, resp.status)
  }
  return resp
}

export const api = {
  summary: (today?: string) =>
    request<import('./types').Summary>(`/api/summary${today ? `?today=${today}` : ''}`),

  calendar: (monthsBack = 3, monthsForward = 6) =>
    request<{
      cells: import('./types').SourceCell[]
      sources: import('./types').SourceAvailability[]
    }>(
      `/api/calendar?months_back=${monthsBack}&months_forward=${monthsForward}`,
    ),

  cycles: () => request<{ cycles: import('./types').CycleView[] }>('/api/cycles'),

  nextRefresh: () => request<import('./types').CycleView>('/api/refresh/next'),

  alerts: () => request<{ alerts: import('./types').Alert[] }>('/api/alerts'),

  roadmap: () => request<import('./types').Roadmap>('/api/roadmap'),

  /** Mueve una tarea de sprint. `sprintId` en null la saca del horizonte. */
  moveTask: (taskId: string, sprintId: string | null) =>
    request<import('./types').MoveResult>(`/api/roadmap/tasks/${taskId}/sprint`, {
      method: 'POST',
      body: JSON.stringify({ sprint_id: sprintId }),
    }),

  /** Devuelve la tarea al sprint que el plan le había asignado. */
  resetTask: (taskId: string) =>
    request<{ ok: boolean; reset: boolean }>(`/api/roadmap/tasks/${taskId}/sprint`, {
      method: 'DELETE',
    }),

  /** Reportes de refresh (BL-050) subidos a una corrida, el más reciente primero. */
  refreshReports: (cycleId: string, period: string) =>
    request<{ reports: import('./types').RefreshReport[] }>(
      `/api/cycles/${cycleId}/${period}/reports`,
    ),

  /** Sube el `refresh_report.html` tal cual lo genera build_refresh_report.py. */
  uploadRefreshReport: async (cycleId: string, period: string, file: File) => {
    const resp = await requestRaw(`/api/cycles/${cycleId}/${period}/reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/html' },
      body: file,
    })
    return (await resp.json()) as { report_id: string }
  },

  /**
   * El HTML del reporte como blob. Un enlace directo no llevaría el token de
   * Clerk, así que se descarga con él y se abre como URL local.
   */
  refreshReportBlob: async (cycleId: string, period: string, reportId: string) => {
    const resp = await requestRaw(`/api/cycles/${cycleId}/${period}/reports/${reportId}`)
    return resp.blob()
  },

  setStage: (cycleId: string, period: string, stageId: string, body: object) =>
    request<{ ok: boolean }>(`/api/cycles/${cycleId}/${period}/stages/${stageId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Borra lo capturado de una etapa y la devuelve a "sin registrar". */
  clearStage: (cycleId: string, period: string, stageId: string) =>
    request<{ ok: boolean }>(`/api/cycles/${cycleId}/${period}/stages/${stageId}`, {
      method: 'DELETE',
    }),

  /** Arranca la consulta a SharePoint. Devuelve DE INMEDIATO (202). */
  startPoll: () =>
    request<import('./types').PollJob>('/api/integrations/poll', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /** Avance del poll en curso, o del último que corrió. */
  pollStatus: () => request<import('./types').PollJob>('/api/integrations/poll'),

  me: () => request<import('./types').Me>('/api/me'),

  /** El Backlog RGM tal como quedó en la última sincronización. */
  backlog: () => request<import('./types').BacklogState>('/api/backlog'),

  /** "Sincronizar ahora": Notion -> herramienta -> ADO. Sólo admin. */
  syncBacklog: () =>
    request<{ error: string | null }>('/api/backlog/sync', { method: 'POST', body: '{}' }),

  /** El enlace a la pantalla de autorización de Notion. Sólo admin. */
  authorizeNotion: () =>
    request<{ url: string }>('/api/integrations/notion/authorize', { method: 'POST', body: '{}' }),

  setValidation: (sourceId: string, period: string, state: string) =>
    request<{ ok: boolean }>(`/api/sources/${sourceId}/${period}/validation`, {
      method: 'POST',
      body: JSON.stringify({ state }),
    }),
}
